import { AlertRuleFormData } from '../types/Alerts/AlertRuleFormState';
import { AlertRuleThreshold } from '../types/Alerts/AlertRules';
import HostDisk from '../types/host/HostDisk';
import HostTarget from '../types/host/HostTarget';
import InstanceTarget from '../types/instance/InstanceTarget';
import Tags from '../types/Tags';

export interface AlertRulePreviewSeverityCounts {
    critical: number;
    warning: number;
    info: number;
}

export interface AlertRulePreviewEntity {
    // Hosts and instances are identified by their numeric id. A disk rule evaluates
    // (host, disk) pairs, which have no id of their own, so those use "<hostid>::<disk>".
    id: number | string;
    name: string;
    type: string;
    monitor: boolean;
    status?: number;
    state?: string;
    // Set for disk entities only, to address the pair when loading its series.
    hostId?: number;
    disk?: string;
    lastValue?: number;
    maxValue?: number;
    severityCounts?: AlertRulePreviewSeverityCounts;
}

export const getDiskEntityId = (hostId: number, disk: string): string => `${hostId}::${disk}`;

const getTagName = (tag: any): string => tag?.tagname ?? tag?.name ?? '';
const getTagValue = (tag: any): string => tag?.tagvalue ?? tag?.value ?? '';

export const getAlertRulePreviewEntities = (
    formData: AlertRuleFormData,
    instances: InstanceTarget[],
    hosts: HostTarget[],
    tags: Tags[],
    disks: HostDisk[] = []
): AlertRulePreviewEntity[] => {
    // The (host, disk) pairs the rule resolves to. The disk list is already narrowed to
    // the selected hosts, so only the disk-name filter is applied here: an empty disk
    // selection means every volume on those hosts, matching the backend's any_disk case.
    if (formData.entitytype === 'disk') {
        const selectedDisks = new Set(formData.disk ?? []);
        const monitoredHostIds = new Set(hosts.filter(host => host.monitor).map(host => host.id));

        return disks
            .filter(disk => selectedDisks.size === 0 || selectedDisks.has(disk.name))
            .filter(disk => monitoredHostIds.size === 0 || monitoredHostIds.has(disk.hostid))
            .map(disk => ({
                id: getDiskEntityId(disk.hostid, disk.name),
                name: `${disk.hostname} — ${disk.name}`,
                type: disk.hosttype,
                monitor: true,
                hostId: disk.hostid,
                disk: disk.name
            }));
    }

    if (formData.entitytype === 'host') {
        const selected = new Set((formData.host ?? []).map(Number));
        return hosts
            .filter(host => host.monitor && (selected.size === 0 || selected.has(host.id)))
            .map(host => ({
                id: host.id,
                name: host.name,
                type: host.type,
                monitor: host.monitor,
                status: host.status,
                state: host.state
            }));
    }

    const selected = new Set((formData.datasource ?? []).map(Number));
    const activeTags = (formData.tag ?? []).filter(tag => getTagName(tag) && getTagValue(tag));
    const tagsByDatasourceId = tags.reduce((acc, tag) => {
        if (tag.datasourceid === undefined) {
            return acc;
        }

        if (!acc.has(tag.datasourceid)) {
            acc.set(tag.datasourceid, []);
        }

        acc.get(tag.datasourceid)?.push(tag);
        return acc;
    }, new Map<number, Tags[]>());
    const datasourceIdsFromTags = new Set<number>();

    if (activeTags.length > 0) {
        tagsByDatasourceId.forEach((datasourceTags, datasourceId) => {
            const matchesAllTags = activeTags.every(ruleTag =>
                datasourceTags.some(tag =>
                    tag.tagname === getTagName(ruleTag) &&
                    tag.tagvalue === getTagValue(ruleTag)
                )
            );

            if (matchesAllTags) {
                datasourceIdsFromTags.add(datasourceId);
            }
        });
    }

    return instances
        .filter(instance => {
            if (!instance.monitor) return false;
            if (selected.size > 0 && !selected.has(instance.id)) return false;
            if (activeTags.length > 0 && !datasourceIdsFromTags.has(instance.id)) return false;
            return true;
        })
        .map(instance => ({
            id: instance.id,
            name: instance.name,
            type: instance.type,
            monitor: instance.monitor,
            status: instance.status,
            state: instance.state
        }));
};

/**
 * Reads the metric value out of one preview API row.
 *
 * Host rows come from the v2 host statistic endpoint, which returns every metric keyed by its
 * statistic name with the aggregation already applied server-side — the same row the alert
 * engine evaluates against. Disk rows follow the same shape, one row per volume timeslice.
 * Datasource rows come from v1, which returns one statistic per call
 * split into avg/sum columns, so the requested aggregation has to be picked here.
 */
export const getAlertRulePreviewRowValue = (row: any, formData: AlertRuleFormData): number => {
    if (formData.entitytype === 'host' || formData.entitytype === 'disk') {
        return Number(row?.[formData.statistic] ?? 0);
    }

    if (formData.metrictype === 'activity') {
        return Number(row?.waittime ?? row?.duration ?? row?.executions ?? 0);
    }

    return Number(row?.[formData.aggregation] ?? row?.sum ?? row?.avg ?? row?.value ?? 0);
};

export const createEmptySeverityCounts = (): AlertRulePreviewSeverityCounts => ({
    critical: 0,
    warning: 0,
    info: 0
});

const getCountKeyForSeverity = (severity: number): keyof AlertRulePreviewSeverityCounts | null => {
    switch (severity) {
        case 1:
            return 'critical';
        case 2:
            return 'warning';
        case 3:
            return 'info';
        default:
            return null;
    }
};

export const getAlertRuleTriggeredCounts = (
    values: number[],
    thresholds: AlertRuleThreshold[],
    decreasing: string
): AlertRulePreviewSeverityCounts => {
    const validThresholds = thresholds
        .filter(threshold => !Number.isNaN(threshold.threshold))
        .sort((a, b) => a.severity - b.severity);
    const counts = createEmptySeverityCounts();

    values.forEach(value => {
        const matchedThreshold = validThresholds.find(threshold =>
            decreasing === 'true'
                ? value <= threshold.threshold
                : value >= threshold.threshold
        );
        const countKey = matchedThreshold ? getCountKeyForSeverity(matchedThreshold.severity) : null;

        if (countKey) {
            counts[countKey] += 1;
        }
    });

    return counts;
};
